import { FormsyRadioGroup, Radio } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import Input from 'components/Form/Input'
import { buildTranslate } from 'locales'
import React from 'react'
import { FormModel, MultiUseRewardsSettings } from '../../FormModel'
import Card from '../Card'
import NextButton from '../NextButton'
import CustomForm from './CustomForm'
import PresetCardGrid from './PresetCardGrid'
import { useMultiUseRewardsState } from './useMultiUseRewardsState'

interface MultiUseRewardsCardProps {
  isCurrent: boolean
  isOpen: boolean
  onNext: () => void
  onFormChange: (newFormModel: FormModel, isChanged: boolean) => void
  settings: MultiUseRewardsSettings
  discount?: string | null
  isBonusPoints?: boolean
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.multi_use_rewards_card'
)

const MultiUseRewardsCard: React.FC<MultiUseRewardsCardProps> = ({
  isCurrent,
  isOpen,
  onNext,
  onFormChange,
  settings,
  discount,
  isBonusPoints = false,
}) => {
  const {
    showCustomForm,
    selectedPreset,
    showMaxIssuances,
    usageType,
    limitType,
    maxIssuances,
    activeWindowDuration,
    maxIssuancesPerWindow,
    windowDuration,
    isNextDisabled,
    estimatedMaxValue,
    summaryText,
    customSummaryText,
    applyPreset,
    toggleMaxIssuances,
    handleCustomizeClick,
    handleBackToPresets,
    handleLimitTypeChange,
  } = useMultiUseRewardsState({ settings, discount, onFormChange })

  return (
    <Card title={t('title')} isOpen={isOpen} className="mb-m">
      <div className="mb-m">
        <FormsyRadioGroup name="multiUseRewards.usageType" value={usageType}>
          <Radio value="exactly_once">
            <Text.BodyText3 color="grey70">{t('exactly_once')}</Text.BodyText3>
          </Radio>
          {usageType === 'exactly_once' && (
            <div className="w-100 pl-l mb-xs">
              <Text.BodyText4 color="grey70">
                {t('exactly_once_description')}
              </Text.BodyText4>
            </div>
          )}
          <Radio value="multiple_times">
            <Text.BodyText3 color="grey70">
              {t('multiple_times')}
            </Text.BodyText3>
          </Radio>
        </FormsyRadioGroup>

        <div
          className="w-100 pl-l"
          style={{
            display: usageType === 'multiple_times' ? undefined : 'none',
          }}
        >
          {usageType === 'multiple_times' && (
            <Text.BodyText4 color="grey70">
              {t('multiple_times_description')}
            </Text.BodyText4>
          )}

          {/* Hidden input for limitType (no FormsyInput in custom form) */}
          <Input
            type="hidden"
            name="multiUseRewards.limitType"
            value={limitType}
          />

          {usageType === 'multiple_times' && !showCustomForm && (
            <PresetCardGrid
              selectedPreset={selectedPreset}
              onPresetSelect={applyPreset}
              onCustomizeClick={handleCustomizeClick}
              summaryText={summaryText}
            />
          )}
          <div style={{ display: showCustomForm ? 'block' : 'none' }}>
            <CustomForm
              limitType={limitType}
              maxIssuancesPerWindow={maxIssuancesPerWindow}
              windowDuration={windowDuration}
              maxIssuances={maxIssuances}
              activeWindowDuration={activeWindowDuration}
              showMaxIssuances={showMaxIssuances}
              isBonusPoints={isBonusPoints}
              isMultipleUse={usageType === 'multiple_times'}
              estimatedMaxValue={estimatedMaxValue}
              customSummaryText={customSummaryText}
              onBackToPresets={handleBackToPresets}
              onLimitTypeChange={handleLimitTypeChange}
              onToggleMaxIssuances={toggleMaxIssuances}
            />
          </div>
        </div>
      </div>
      {isCurrent && <NextButton onClick={onNext} disabled={isNextDisabled} />}
    </Card>
  )
}

export default MultiUseRewardsCard
